import React, { useEffect, useState } from "react";
import axios from "axios";
import backendUrl from "../../BackendUrl";

const RightPanel = ({
  selectedTrim,
  selectedColor,
  setSelectedColor,
  selectedAccessories,
  setSelectedAccessories,
  selectedPackage,
  setSelectedPackage,
  selectedOffer,
  setSelectedOffer,
  rightTab,
  setRightTab,
}) => {
  const [colors, setColors] = useState([]);
  const [accessories, setAccessories] = useState([]);
  const [packages, setPackages] = useState([]);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch colors, accessories, packages when trim changes
  useEffect(() => {
    if (!selectedTrim) {
      setColors([]);
      setAccessories([]);
      setPackages([]);
      return;
    }
    setColors(selectedTrim.colors || []);
    setLoading(true);
    Promise.all([
      axios.get(`${backendUrl}/api/vehicles/get-package`, {
        params: { trimIds: selectedTrim._id },
      }),
      axios.get(`${backendUrl}/api/vehicles/get-accessory`, {
        params: { trimIds: selectedTrim._id },
      }),
    ])
      .then(([pkgRes, accRes]) => {
        setPackages(pkgRes.data.filter(item => item.ready));
        setAccessories(accRes.data.filter(item => item.ready));
      })
      .finally(() => setLoading(false));
  }, [selectedTrim]);

  // Fetch offers on mount
  useEffect(() => {
    axios
      .get(`${backendUrl}/api/offers/get-offer`)
      .then((res) =>
        setOffers(Array.isArray(res.data) ? res.data.filter((o) => o.ready) : [])
      );
  }, []);

  // Color Picker
  const renderColorPicker = () => (
    <div className="selection-card">
      <h4>Choose Color</h4>
      <div className="scrollable-picker">
        {colors.map((color) => (
          <button
            key={color._id || color.color}
            className={`select-btn${selectedColor && selectedColor.color === color.color ? " selected" : ""}`}
            onClick={() => setSelectedColor(color)}
          >
            <div className="section-btn-title">{color.color}</div>
            {color.colorImg && (
              <img src={color.colorImg} alt={color.color} className="section-btn-img" />
            )}
          </button>
        ))}
      </div>
    </div>
  );

  // Accessories Picker
  const renderAccessoriesPicker = () => (
    <div className="selection-card">
      <h4>Accessories</h4>
      {accessories.map((acc) => {
        const isSelected = selectedAccessories.some((item) => item._id === acc._id);
        // Disable if accessory is included in selectedPackage.accessories (by name)
        const isDisabled =
          selectedPackage &&
          Array.isArray(selectedPackage.accessories) &&
          selectedPackage.accessories.includes(acc.accessory);

        return (
          <button
            key={acc._id}
            className={`select-btn${isSelected ? " selected" : ""}`}
            onClick={() => {
              setSelectedAccessories((prev) =>
                isSelected
                  ? prev.filter((item) => item._id !== acc._id)
                  : [...prev, acc]
              );
            }}
            disabled={isDisabled}
          >
            <div className="section-btn-title">{acc.accessory}</div>
            {acc.accessoryImg && (
              <img src={acc.accessoryImg} alt={acc.accessory} className="section-btn-img" />
            )}
            <div className="section-btn-price">
              {acc.price ? `$${acc.price.toLocaleString()}` : ""}
            </div>
          </button>
        );
      })}
    </div>
  );

  // Packages Picker (uses accessories by name)
  const renderPackagesPicker = () => (
    <div className="selection-card">
      <h4>Packages</h4>
      {/* Removed scrollable-picker here */}
      {packages.map((pkg) => {
        const isSelected = selectedPackage && selectedPackage._id === pkg._id;
        // Disable if any selected accessory is included in this package (by name)
        const isDisabled =
          selectedAccessories.length > 0 &&
          Array.isArray(pkg.accessories) &&
          selectedAccessories.some((acc) => pkg.accessories.includes(acc.accessory));

        // Included accessory names (by name)
        const includedAccessoryNames = Array.isArray(pkg.accessories)
          ? pkg.accessories
          : [];

        return (
          <button
            key={pkg._id}
            className={`select-btn${isSelected ? " selected" : ""}`}
            onClick={() => setSelectedPackage(isSelected ? null : pkg)}
            disabled={isDisabled}
          >
            <div className="section-btn-title">{pkg.package}</div>
            {pkg.packageImg && (
              <img src={pkg.packageImg} alt={pkg.package} className="section-btn-img" />
            )}
            <div className="section-btn-price">
              {pkg.price ? `$${pkg.price.toLocaleString()}` : ""}
            </div>
            <div className="section-btn-desc">{pkg.description}</div>
            {includedAccessoryNames.length > 0 && (
              <div className="package-accessories">
                <strong>Includes:</strong> {includedAccessoryNames.join(", ")}
              </div>
            )}
          </button>
        );
      })}
    </div>
  );

  // Offers Picker (can unselect by clicking again)
  const renderOffersPicker = () => (
    <div className="selection-card">
      <h4>Offers</h4>
      <div className="scrollable-picker">
        {offers.map((offer) => {
          const isSelected = selectedOffer && selectedOffer._id === offer._id;
          return (
            <button
              key={offer._id}
              className={`select-btn${isSelected ? " selected" : ""}`}
              onClick={() => setSelectedOffer(isSelected ? null : offer)}
            >
              <div className="section-btn-title">{offer.offer}</div>
              {offer.offerImg && (
                <img src={offer.offerImg} alt={offer.offer} className="section-btn-img" />
              )}
              <div className="section-btn-desc">{offer.description}</div>
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <aside className="right-panel">
      <div className="right-tabs">
        <button
          className={rightTab === "options" ? "active" : ""}
          onClick={() => setRightTab("options")}
        >
          Options
        </button>
        <button
          className={rightTab === "offers" ? "active" : ""}
          onClick={() => setRightTab("offers")}
        >
          Offers
        </button>
      </div>
      <div className="right-content">
        {loading && <div>Loading...</div>}
        {rightTab === "options" ? (
          <>
            {renderColorPicker()}
            {renderAccessoriesPicker()}
            {renderPackagesPicker()}
          </>
        ) : (
          renderOffersPicker()
        )}
      </div>
    </aside>
  );
};

export default RightPanel;